import { Link, useSearchParams } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'

import {
  getBlogCategoryLabel,
  getBlogCategorySlug,
  getBlogPostDateLabel,
  getBlogReadTimeLabel,
  resolveBlogCategory,
} from '@/data/blog'

import { BlogPostCard } from '@/components/blog/blog-post-card'
import { JsonLd } from '@/components/seo/json-ld'
import { SeoHead } from '@/components/seo/seo-head'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { SectionHeading } from '@/components/ui/section-heading'
import { getPublicBlogIndexData } from '@/lib/blog/server'
import { cn } from '@/lib/utils'
import { createMetadata, getBlogSchema, marketingSeoPages } from '@/lib/seo'

const metadata = createMetadata(marketingSeoPages.blog)

export default function BlogIndexPage() {
  const [searchParams] = useSearchParams()
  const blogIndexData = getPublicBlogIndexData()
  const activeCategory = resolveBlogCategory(searchParams.get('category'))
  const activeCategoryLabel = activeCategory ? getBlogCategoryLabel(activeCategory) : null
  const filteredPosts = activeCategory
    ? blogIndexData.posts.filter((post) => post.category === activeCategory)
    : blogIndexData.posts
  const heroPost = activeCategory
    ? filteredPosts[0] ?? blogIndexData.featuredPosts[0] ?? blogIndexData.posts[0]
    : blogIndexData.featuredPosts[0] ?? blogIndexData.posts[0]
  const libraryPosts = filteredPosts.filter((post) => post.slug !== heroPost?.slug)

  return (
    <div className="marketing-shell space-y-14 pb-20 pt-12">
      <SeoHead metadata={metadata} />
      <JsonLd
        data={getBlogSchema({
          title: 'KindLumen donation form blog',
          description: marketingSeoPages.blog.description,
          path: marketingSeoPages.blog.path,
        })}
      />

      <section className="panel overflow-hidden p-8 lg:p-10">
        <div className="grid gap-10 xl:grid-cols-[1.02fr_0.98fr] xl:items-start">
          <div className="space-y-8">
            <SectionHeading
              as="h1"
              eyebrow="KindLumen blog"
              title="Build and evaluate a Stripe donation experience."
              description="Practical guidance for accepting Stripe donations, embedding a form on your website, and evaluating donation-platform tradeoffs."
            />

            <div className="rounded-[28px] border border-line bg-white/80 p-5">
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/blog"
                  className={cn(
                    'inline-flex items-center rounded-full border px-4 py-2 text-sm font-semibold transition',
                    !activeCategory
                      ? 'border-brand/20 bg-brand/10 text-brand'
                      : 'border-line bg-white text-ink-soft hover:border-brand/25 hover:text-ink',
                  )}
                >
                  All articles
                </Link>
                {blogIndexData.categoryCounts.map((entry) => (
                  <Link
                    key={entry.category}
                    to={`/blog?category=${getBlogCategorySlug(entry.category)}`}
                    className={cn(
                      'inline-flex items-center rounded-full border px-4 py-2 text-sm font-semibold transition',
                      activeCategory === entry.category
                        ? 'border-brand/20 bg-brand/10 text-brand'
                        : 'border-line bg-white text-ink-soft hover:border-brand/25 hover:text-ink',
                    )}
                  >
                    {getBlogCategoryLabel(entry.category)}
                  </Link>
                ))}
              </div>

              <p className="mt-4 text-sm leading-6 text-ink-soft">
                Browse comparisons, implementation guides, and nonprofit fundraising topics, then continue into {link('/features', 'features')} or {link('/pricing', 'pricing')} if you want to evaluate KindLumen more closely.
              </p>
            </div>
          </div>

          {heroPost ? (
            <Link to={`/blog/${heroPost.slug}`} className="group block h-full">
              <div className="overflow-hidden rounded-[30px] border border-line bg-white shadow-soft">
                <div className="relative aspect-[16/10] overflow-hidden border-b border-line">
                  <img
                    src={heroPost.featuredImageUrl}
                    alt={heroPost.featuredImageAlt}
                    className="object-cover transition duration-300 group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                </div>

                <div className="space-y-5 p-7 sm:p-8">
                  <div className="flex flex-wrap items-center gap-3">
                    <Badge>Featured guide</Badge>
                    <span className="text-xs font-medium uppercase tracking-[0.14em] text-ink-muted">
                      {getBlogReadTimeLabel(heroPost)}
                    </span>
                  </div>

                  <div className="space-y-4">
                    <h2 className="text-3xl font-semibold leading-[1.08] text-ink sm:text-[2.5rem]">
                      {heroPost.title}
                    </h2>
                    <p className="max-w-2xl text-base leading-7 text-ink-soft">{heroPost.excerpt}</p>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-ink-muted">
                    <span>By {heroPost.author}</span>
                    <span>{getBlogPostDateLabel(heroPost)}</span>
                    <span>{getBlogCategoryLabel(heroPost.category)}</span>
                  </div>

                  <div className="inline-flex items-center gap-2 text-sm font-semibold text-brand">
                    Read article
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              </div>
            </Link>
          ) : null}
        </div>
      </section>

      {libraryPosts.length > 0 ? (
        <section>
          <SectionHeading
            eyebrow={activeCategoryLabel ?? 'Reviewed guides'}
            title={activeCategoryLabel ? `More from ${activeCategoryLabel}.` : 'Continue with the other reviewed guide.'}
            description="Every article published here has passed the current sourcing and product-verification standard."
            actions={
              <>
                <Button asChild variant="secondary">
                  <Link to="/features">View features</Link>
                </Button>
                <Button asChild>
                  <Link to="/pricing">View pricing</Link>
                </Button>
              </>
            }
          />

          <div className={cn('mt-8 grid gap-6', libraryPosts.length > 1 && 'md:grid-cols-2 xl:grid-cols-3')}>
            {libraryPosts.map((post) => (
              <BlogPostCard key={post.slug} post={post} horizontal={libraryPosts.length === 1} />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  )
}

function link(href: string, label: string) {
  return <Link to={href} className="font-semibold text-brand hover:text-brand/80">{label}</Link>
}

export const Component = BlogIndexPage
